import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Lock, MapPin } from 'lucide-react';
const WEB3FORMS_ACCESS_KEY = '06983205-32ed-43af-a56a-c423e49925cb';
const SERVICE_OPTIONS = ['Book a Party', 'Swedish Massage', 'Deep Tissue', 'Hot Stone', 'Aromatherapy', 'Couples Massage', 'VIP Couples Package', 'Nuru Massage', 'Sensual Full-Body', 'Four-Hands Symphony', 'Tantric Journey', 'Couples Ritual', 'Bespoke Request'];
type FormStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Reservations() {
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setErrorMessage('');
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);
    try {
      // Send as multipart/form-data (matches Web3Forms' native HTML snippet)
      // — avoids the CORS preflight that JSON requests trigger.
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData
      });
      const result = await response.json();
      if (result.success) {
        setFormStatus('success');
        formEl.reset();
      } else {
        setFormStatus('error');
        setErrorMessage(result.message || 'Something went wrong sending your request. Please try again or call us directly.');
        // eslint-disable-next-line no-console
        console.error('Web3Forms error:', result);
      }
    } catch (err) {
      setFormStatus('error');
      setErrorMessage('Unable to reach our booking system. If you are testing inside the editor preview, try the published site — preview iframes sometimes block external requests. Otherwise please call us directly.');
      // eslint-disable-next-line no-console
      console.error('Network error reaching Web3Forms:', err);
    }
  };
  return <section id="reservations" className="py-24 md:py-32 relative" data-id="element-384">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-385">
        <motion.div initial={{
        opacity: 0,
        x: -30
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} data-id="element-386">
          <span className="text-rose-400 uppercase tracking-[0.2em] text-sm mb-4 block" data-id="element-387">
            Book Your Session
          </span>
          <h2 className="font-serif text-4xl md:text-5xl text-lavender-50 mb-6" data-id="element-388">
            Reserve Your <br data-id="element-389" />
            <span className="italic bg-gradient-to-r from-lavender-300 to-rose-400 bg-clip-text text-transparent" data-id="element-390">
              Sanctuary
            </span>
          </h2>
          <p className="text-lavender-100/75 font-light leading-relaxed text-lg mb-8" data-id="element-391">
            Every visit to Afriel is fully private and discreet. To honor your
            privacy and prepare your suite, all sessions are by appointment only
            with a minimum of 24 hours notice.
          </p>

          <div className="space-y-6 mb-12" data-id="element-392">
            <div className="flex gap-4 p-5 rounded-xl bg-plum-900/40 border border-lavender-400/10" data-id="element-393">
              <Lock className="text-rose-400 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} data-id="element-394" />
              <div data-id="element-395">
                <h4 className="text-lavender-50 font-medium mb-1 uppercase tracking-widest text-sm" data-id="element-396">
                  100% Discreet
                </h4>
                <p className="text-lavender-100/60 font-light text-sm" data-id="element-397">
                  Every session is completely private. All communications and
                  bookings are strictly confidential.
                </p>
              </div>
            </div>
            <div className="flex gap-4 p-5 rounded-xl bg-plum-900/40 border border-lavender-400/10" data-id="element-398">
              <MapPin className="text-rose-400 flex-shrink-0 mt-1" size={20} strokeWidth={1.5} data-id="element-399" />
              <div data-id="element-400">
                <h4 className="text-lavender-50 font-medium mb-1 uppercase tracking-widest text-sm" data-id="element-401">
                  LA Location
                </h4>
                <p className="text-lavender-100/60 font-light text-sm" data-id="element-402">
                  Our private sanctuary is located in the Los Angeles area.
                  Exact address shared upon booking confirmation.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: 30
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="bg-gradient-to-br from-plum-900/60 to-plum-800/40 p-8 md:p-10 rounded-2xl border border-lavender-400/20 backdrop-blur-sm" data-id="element-403">
          {formStatus === 'success' ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-404">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-lavender-500 to-rose-500 flex items-center justify-center mb-6" data-id="element-405">
                <span className="text-white text-2xl" data-id="element-406">✓</span>
              </div>
              <h3 className="font-serif text-2xl text-lavender-50 mb-4" data-id="element-407">
                Request Received
              </h3>
              <p className="text-lavender-100/75 font-light" data-id="element-408">
                Your reservation request was sent to our concierge. We'll be in
                touch shortly to confirm your booking and discuss any bespoke
                requests.
              </p>
              <button onClick={() => setFormStatus('idle')} className="mt-8 text-rose-300 hover:text-rose-200 text-sm uppercase tracking-widest transition-colors" data-id="element-409">
                Send Another Request
              </button>
            </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-410">
              {/* Web3Forms required fields */}
              <input type="hidden" name="access_key" value={WEB3FORMS_ACCESS_KEY} data-id="element-411" />
              <input type="hidden" name="subject" value="New Reservation Request — Afriel Spa" data-id="element-412" />
              <input type="hidden" name="from_name" value="Afriel Spa Website" data-id="element-413" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-414">
                <div className="space-y-2" data-id="element-415">
                  <label className="text-xs uppercase tracking-widest text-lavender-200/70" data-id="element-416">
                    Name
                  </label>
                  <input required name="name" type="text" className="w-full bg-transparent border-b border-lavender-400/30 pb-2 text-lavender-50 focus:outline-none focus:border-rose-400 transition-colors" data-id="element-417" />
                </div>
                <div className="space-y-2" data-id="element-418">
                  <label className="text-xs uppercase tracking-widest text-lavender-200/70" data-id="element-419">
                    Email
                  </label>
                  <input required name="email" type="email" placeholder="you@example.com" className="w-full bg-transparent border-b border-lavender-400/30 pb-2 text-lavender-50 focus:outline-none focus:border-rose-400 transition-colors placeholder:text-lavender-200/30" data-id="element-420" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-421">
                <div className="space-y-2" data-id="element-422">
                  <label className="text-xs uppercase tracking-widest text-lavender-200/70" data-id="element-423">
                    Preferred Date
                  </label>
                  <input name="preferred_date" type="date" className="w-full bg-transparent border-b border-lavender-400/30 pb-2 text-lavender-50 focus:outline-none focus:border-rose-400 transition-colors [color-scheme:dark]" data-id="element-424" />
                </div>
                <div className="space-y-2" data-id="element-425">
                  <label className="text-xs uppercase tracking-widest text-lavender-200/70" data-id="element-426">
                    Type of Service
                  </label>
                  <select name="type_of_service" defaultValue={SERVICE_OPTIONS[0]} className="w-full bg-transparent border-b border-lavender-400/30 pb-2 text-lavender-50 focus:outline-none focus:border-rose-400 transition-colors" data-id="element-427">
                    {SERVICE_OPTIONS.map(option => <option key={option} value={option} className="bg-plum-900 text-lavender-50" data-id="element-428">
                        {option}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2" data-id="element-429">
                <label className="text-xs uppercase tracking-widest text-lavender-200/70" data-id="element-430">
                  Message
                </label>
                <textarea required name="message" rows={4} placeholder="Share any special requests, preferred time, or questions." className="w-full bg-transparent border-b border-lavender-400/30 pb-2 text-lavender-50 focus:outline-none focus:border-rose-400 transition-colors resize-none placeholder:text-lavender-200/30" data-id="element-431"></textarea>
              </div>

              {formStatus === 'error' && <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-400/30 text-rose-200 text-sm" data-id="element-432">
                  {errorMessage}
                </div>}

              <button type="submit" disabled={formStatus === 'submitting'} className="w-full py-4 bg-gradient-to-r from-lavender-500 to-rose-500 text-white uppercase tracking-widest text-sm rounded-full hover:shadow-2xl hover:shadow-lavender-500/40 transition-all duration-300 disabled:opacity-50" data-id="element-433">
                {formStatus === 'submitting' ? 'Sending...' : 'Submit'}
              </button>

              <p className="text-[11px] text-lavender-200/50 font-light text-center" data-id="element-434">
                Your reservation will be sent securely to our concierge.
              </p>
            </form>}
        </motion.div>
      </div>
    </section>;
}